// Store profile: name, address, contact, timezone and the trading-day close
// — a full PUT of the store — beside the public view ordering clients see
// signed out. Channel switches live on Ordering & checkout and the pause
// switch on Hours & closures; the PUT carries both through untouched.

import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useBase } from '../../scope';
import { Err } from './controls';
import { Check, errMsg, Head, SaveBtn, useAction } from './shared';
import { storeRequest, type Store, type StorePublic } from './types';

const ZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide',
  'Australia/Darwin', 'Australia/Perth', 'Australia/Hobart',
];

export function Profile() {
  const base = useBase();
  const [form, setForm] = useState<Store | null>(null);
  const [pub, setPub] = useState<StorePublic | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const save = useAction();

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get<Store>(base), api.get<StorePublic>(`${base}/public`)]);
      setForm(s);
      setPub(p);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errMsg(e));
    }
  }, [base]);
  useEffect(() => { void load(); }, [load]);

  const set = <K extends keyof Store>(k: K, v: Store[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const doSave = () => {
    if (!form) return;
    void save.run(async () => {
      await api.put<Store>(base, storeRequest({ ...form, name: form.name.trim(), address: form.address.trim(), phoneE164: form.phoneE164.trim() }));
      await load();
    });
  };

  return (
    <>
      <Head title="Store profile" />
      <Err error={loadErr} />
      {!form && !loadErr && <div className="loading">Loading…</div>}
      {form && (
        <div className="set-cols">
          <div className="panel" data-section="profile">
            <h2>Profile</h2>
            <div className="set-stack" style={{ paddingTop: 8 }}>
              <div className="frow">
                <div className="field">
                  <label>Store name</label>
                  <input className="input" aria-label="Store name" value={form.name} onChange={(e) => set('name', e.target.value)} />
                </div>
              </div>
              <div className="frow">
                <div className="field">
                  <label>Address</label>
                  <input className="input" aria-label="Address" value={form.address} onChange={(e) => set('address', e.target.value)} />
                </div>
              </div>
              <div className="frow">
                <div className="field">
                  <label>Phone (E.164)</label>
                  <input className="input" aria-label="Phone" value={form.phoneE164} placeholder="+61…"
                    onChange={(e) => set('phoneE164', e.target.value)} />
                </div>
                <div className="field">
                  <label>Timezone</label>
                  <select className="input" aria-label="Timezone" value={form.timezone} onChange={(e) => set('timezone', e.target.value)}>
                    {(ZONES.includes(form.timezone) ? ZONES : [form.timezone, ...ZONES]).map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              </div>
              <div className="frow">
                <div className="field narrow" style={{ flexBasis: 150 }}>
                  <label>Trading day closes</label>
                  <input className="input" type="time" aria-label="Day close time" value={form.dayCloseTime}
                    onChange={(e) => set('dayCloseTime', e.target.value)} />
                </div>
                <div className="field">
                  <span className="hint">
                    The trading day runs from this time to the same time next day, store-local —
                    every report&rsquo;s business date follows it.
                  </span>
                </div>
              </div>
              <Check label="Permanently closed" checked={form.permanentlyClosed} onChange={(v) => set('permanentlyClosed', v)} />
              <Err error={save.error} />
              <div>
                <SaveBtn busy={save.busy} saved={save.saved} onClick={doSave} label="Save profile"
                  disabled={!form.name.trim() || !form.address.trim() || !form.phoneE164.trim()} />
              </div>
            </div>
          </div>

          {pub && (
            <div className="panel" data-section="public">
              <h2>What customers see</h2>
              <div className="hint" style={{ margin: '6px 0 10px' }}>
                The public store record — what the ordering site reads without signing in.
              </div>
              <div className="rowline"><div className="grow"><span className="sub">Name</span><b data-field="name">{pub.name}</b></div></div>
              <div className="rowline"><div className="grow"><span className="sub">Address</span><b data-field="address">{pub.address}</b></div></div>
              <div className="rowline"><div className="grow"><span className="sub">Phone</span><b data-field="phone">{pub.phoneE164}</b></div></div>
              <div className="rowline"><div className="grow"><span className="sub">Timezone</span><b data-field="timezone">{pub.timezone}</b></div></div>
              <div className="rowline">
                <div className="grow">
                  <span className="sub">Channels</span>
                  <b data-field="channels">
                    {[pub.pickupEnabled && 'Pickup', pub.deliveryEnabled && 'Delivery', pub.dineInEnabled && 'Dine-in'].filter(Boolean).join(' · ') || 'None'}
                  </b>
                </div>
              </div>
              <div className="rowline">
                <div className="grow">
                  <span className="sub">Status</span>
                  <b data-field="status">
                    {pub.permanentlyClosed ? 'Permanently closed' : pub.temporarilyClosed ? 'Temporarily closed' : 'Trading'}
                  </b>
                </div>
                <span className={`chip ${pub.onlinePaymentsEnabled ? 'chip-paid' : 'chip-unpaid'}`}>
                  {pub.onlinePaymentsEnabled ? 'online payments on' : 'online payments off'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
